import React, { useMemo } from 'react';
import { Table } from '@mantine/core';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { SectionKey } from '../../shared/types/dashboard-page.types';
import { asCurrency, asDate, asHour } from '../../shared/utils/dashboard-page.utils';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import classes from '../PaymentMethodSection/payment-method-section.module.scss';

export type RefundsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  payments: AdminPaymentlineView[];
};

type RefundRow = {
  orderCode: string;
  orderDisplay: string;
  dateDisplay: string;
  creditRefund: number;
  cashRefund: number;
  totalRefund: number;
  lastUpdatedAtMs: number;
};

const getOrderNumber = (orderCode: string): string => {
  const parts = orderCode.split('/');
  if (parts.length >= 2) {
    return parts.slice(-2).join('/');
  }
  return orderCode;
};

const buildDateDisplay = (iso: string): string => `${asDate(iso)} ${asHour(iso)}`;

const isRefundLine = (row: AdminPaymentlineView): boolean => row.status === 'done' && row.amount < 0;

const buildRefundRows = (payments: AdminPaymentlineView[]): RefundRow[] => {
  const byOrder = new Map<string, RefundRow>();

  payments
    .filter(isRefundLine)
    .forEach((row) => {
      const key = row.orderCode;
      const amount = Math.abs(row.amount);
      const updatedMs = new Date(row.updatedAt).getTime();
      const safeUpdatedMs = Number.isNaN(updatedMs) ? 0 : updatedMs;
      const existing = byOrder.get(key);

      if (!existing) {
        byOrder.set(key, {
          orderCode: key,
          orderDisplay: getOrderNumber(row.orderCode),
          dateDisplay: buildDateDisplay(row.updatedAt),
          creditRefund: row.method === 'credit' ? amount : 0,
          cashRefund: row.method === 'cash' ? amount : 0,
          totalRefund: amount,
          lastUpdatedAtMs: safeUpdatedMs,
        });
        return;
      }

      if (row.method === 'credit') {
        existing.creditRefund += amount;
      } else {
        existing.cashRefund += amount;
      }
      existing.totalRefund += amount;

      if (safeUpdatedMs > existing.lastUpdatedAtMs) {
        existing.lastUpdatedAtMs = safeUpdatedMs;
        existing.dateDisplay = buildDateDisplay(row.updatedAt);
      }
    });

  return [...byOrder.values()].sort((a, b) => b.lastUpdatedAtMs - a.lastUpdatedAtMs);
};

export const RefundsSection: React.FC<RefundsSectionProps> = ({
  collapsedSections,
  toggleSection,
  payments,
}) => {
  const rows = useMemo(() => buildRefundRows(payments), [payments]);

  const totals = useMemo(() => rows.reduce(
    (acc, row) => ({
      creditRefund: acc.creditRefund + row.creditRefund,
      cashRefund: acc.cashRefund + row.cashRefund,
      totalRefund: acc.totalRefund + row.totalRefund,
    }),
    { creditRefund: 0, cashRefund: 0, totalRefund: 0 },
  ), [rows]);

  return (
    <SectionCard title="החזרים" sectionKey="refunds" collapsed={collapsedSections.refunds} onToggle={() => toggleSection('refunds')}>
      <div className={classes.tableScroll}>
        <Table className={`${classes.table} ${classes.tableScrollable}`}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th>סה"כ</Table.Th>
              <Table.Th>החזר מזומן</Table.Th>
              <Table.Th>החזר אשראי</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.orderCode}>
                <Table.Th className={classes.sessionCell}>
                  {row.orderDisplay}
                  <span className={classes.sessionTime}>{row.dateDisplay}</span>
                </Table.Th>
                <Table.Td>{asCurrency(row.totalRefund)}</Table.Td>
                <Table.Td>{asCurrency(row.cashRefund)}</Table.Td>
                <Table.Td>{asCurrency(row.creditRefund)}</Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr className={classes.totalRow}>
              <Table.Th className={classes.sessionCell}>סה"כ</Table.Th>
              <Table.Td>{asCurrency(totals.totalRefund)}</Table.Td>
              <Table.Td>{asCurrency(totals.cashRefund)}</Table.Td>
              <Table.Td>{asCurrency(totals.creditRefund)}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </div>
    </SectionCard>
  );
};
